import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import RouteCard from "@/components/rotta/RouteCard";
import CityCard from "@/components/rotta/CityCard";
import EventCard from "@/components/rotta/EventCard";
import BlogCard from "@/components/rotta/BlogCard";
import NewsletterCTA from "@/components/rotta/NewsletterCTA";
import NewsletterBand from "@/components/rotta/NewsletterBand";
import { fetchRoutes, fetchCities, fetchEvents, fetchBlog } from "@/lib/api";

const HERO_IMAGE =
  "https://images.pexels.com/photos/29459994/pexels-photo-29459994.jpeg?auto=compress&cs=tinysrgb&w=2400&q=80";
const HERO_FALLBACK =
  "https://customer-assets.emergentagent.com/job_rotas-descoberta/artifacts/82bd79ke_image_processing20201203-13867-6zbog4.jpeg";

export default function Home() {
  const [routes, setRoutes] = useState([]);
  const [cities, setCities] = useState([]);
  const [events, setEvents] = useState([]);
  const [blog, setBlog] = useState([]);

  useEffect(() => {
    fetchRoutes({ featured: true }).then(setRoutes).catch(() => {});
    fetchCities().then(setCities).catch(() => {});
    fetchEvents().then(setEvents).catch(() => {});
    fetchBlog().then(setBlog).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative h-[88vh] min-h-[640px] w-full overflow-hidden flex items-end"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            onError={(e) => { e.currentTarget.src = HERO_FALLBACK; }}
            alt="Grupo de motociclistas em estrada sinuosa ao entardecer"
            className="w-full h-full object-cover slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/55 via-[#0B0B0B]/30 to-[#0B0B0B]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B]/80 via-transparent to-transparent" />
        </div>

        <div className="container-rotta relative z-10 pb-20 md:pb-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6 fade-up">
              <span className="gold-divider" />
              <span className="text-[11px] tracking-[0.35em] uppercase text-[#C6A762]">
                Plataforma de Mototurismo · Brasil
              </span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.95] text-balance fade-up delay-1">
              As melhores rotas de mototurismo
              <br />
              <span className="text-gold-gradient">do Brasil, em um só lugar.</span>
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-2xl leading-relaxed fade-up delay-2">
              Descubra rotas verificadas, cidades, eventos, pontos de interesse e experiências
              cuidadosamente organizadas para quem vive a estrada.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3 sm:gap-4 fade-up delay-3">
              <Link
                to="/rotas"
                data-testid="hero-cta-primary"
                className="btn-gold px-7 py-4 rounded-full text-xs inline-flex items-center gap-2"
              >
                Explorar Rotas <ArrowRight size={14} />
              </Link>
              <Link
                to="/cidades"
                data-testid="hero-cta-cities"
                className="btn-gold-outline px-7 py-4 rounded-full text-xs inline-flex items-center gap-2"
              >
                Explorar Cidades <ArrowRight size={14} />
              </Link>
              <Link
                to="/sobre"
                data-testid="hero-cta-secondary"
                className="btn-ghost px-7 py-4 rounded-full text-xs inline-flex items-center gap-2"
              >
                Conheça a ROTTA
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER — faixa compacta logo abaixo do Hero */}
      <NewsletterBand />

      {/* ROTAS EM DESTAQUE */}
      <section className="section" data-testid="featured-routes-section">
        <div className="container-rotta">
          <Header
            kicker="Rotas em Destaque"
            title="Onde a estrada te chama agora."
            cta={{ to: "/rotas", label: "Ver todas as rotas" }}
          />
          <div className="mt-10 grid gap-7 md:grid-cols-2 lg:grid-cols-3">
            {routes.map((r) => (
              <RouteCard key={r.slug} route={r} />
            ))}
          </div>
        </div>
      </section>

      {/* CIDADES EM DESTAQUE */}
      <section className="section pt-0" data-testid="featured-cities-section">
        <div className="container-rotta">
          <Header
            kicker="Cidades em Destaque"
            title="Destinos para a parada perfeita."
            cta={{ to: "/cidades", label: "Ver cidades" }}
          />
          <div className="mt-10 grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
            {cities.slice(0, 4).map((c) => (
              <CityCard key={c.slug} city={c} />
            ))}
          </div>
        </div>
      </section>

      {/* PRÓXIMOS EVENTOS */}
      <section className="section pt-0" data-testid="upcoming-events-section">
        <div className="container-rotta">
          <Header
            kicker="Próximos Eventos"
            title="Encontros que valem a viagem."
            cta={{ to: "/eventos", label: "Ver agenda" }}
          />
          <div className="mt-10 grid gap-7 md:grid-cols-2">
            {events.slice(0, 2).map((e) => (
              <EventCard key={e.slug} event={e} />
            ))}
          </div>
        </div>
      </section>

      {/* ÚLTIMOS ARTIGOS */}
      <section className="section pt-0" data-testid="latest-articles-section">
        <div className="container-rotta">
          <Header
            kicker="Diário ROTTA"
            title="Últimos artigos do blog."
            cta={{ to: "/blog", label: "Ler blog" }}
          />
          <div className="mt-10 grid gap-7 md:grid-cols-2 lg:grid-cols-3">
            {blog.slice(0, 3).map((p) => (
              <BlogCard key={p.slug} post={p} />
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <NewsletterCTA />

      {/* SOBRE */}
      <section className="section pt-0" data-testid="home-about-section">
        <div className="container-rotta grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="img-zoom rounded-2xl overflow-hidden border border-[#1f1f1f]">
              <img
                src="https://images.unsplash.com/photo-1558981852-426c6c22a060?auto=format&fit=crop&w=1600&q=85"
                alt="Estrada brasileira ao entardecer"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 text-[#C6A762] mb-5">
              <span className="gold-divider" />
              <span className="text-[11px] tracking-[0.35em] uppercase">O que é a ROTTA</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05]">
              A plataforma do <span className="text-gold-gradient">mototurismo brasileiro.</span>
            </h2>
            <p className="text-[#B8B8B8] mt-5 leading-relaxed">
              A ROTTA nasceu para conectar motociclistas às melhores estradas, destinos e
              experiências do Brasil. Mais do que um catálogo de rotas, a plataforma reúne trajetos
              verificados, cidades, pontos de interesse, eventos e conteúdos produzidos por quem
              realmente vive a estrada.
            </p>
            <div className="grid sm:grid-cols-3 gap-6 mt-9">
              <Value title="Liberdade" desc="Viva sua própria rota." />
              <Value title="Exploração" desc="Descubra novos lugares." />
              <Value title="Conexão" desc="Encontre sua tribo." />
            </div>
            <Link
              to="/sobre"
              data-testid="home-about-cta"
              className="btn-ghost inline-flex items-center gap-2 mt-10 px-6 py-3 rounded-full text-xs"
            >
              Conheça a ROTTA <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Header({ kicker, title, cta }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
      <div className="max-w-2xl">
        <div className="flex items-center gap-3 text-[#C6A762] mb-4">
          <span className="gold-divider" />
          <span className="text-[11px] tracking-[0.35em] uppercase">{kicker}</span>
        </div>
        <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05]">{title}</h2>
      </div>
      {cta && (
        <Link to={cta.to} className="text-[#C6A762] text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2 hover:translate-x-1 transition-transform">
          {cta.label} <ArrowRight size={14} />
        </Link>
      )}
    </div>
  );
}

function Value({ title, desc }) {
  return (
    <div className="border-l border-[#C6A762]/40 pl-4">
      <div className="font-display tracking-wider text-white uppercase text-sm">{title}</div>
      <div className="text-[#B8B8B8] text-sm mt-1">{desc}</div>
    </div>
  );
}
